import React, { useState } from 'react';
import Card from "components/card";
import { MdInventory, MdInfo, MdExpandMore, MdExpandLess, MdSearch, MdVisibility } from "react-icons/md";
import { useNavigate } from 'react-router-dom';

// Modern color palette with better contrast
const COLORS = [
  '#2563EB', // Blue
  '#059669', // Green
  '#D97706', // Amber
  '#DC2626', // Red
  '#7C3AED', // Purple
  '#DB2777', // Pink
  '#0891B2', // Cyan
  '#EA580C', // Orange
  '#4F46E5', // Indigo
  '#0D9488', // Teal
];

const ProductDistributionPieChart = ({ products }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  // Calculate total actual stock
  const totalActualStock = products.reduce((sum, product) => sum + Math.abs(parseInt(product.actual_stock)), 0);

  // Prepare data and sort by actual stock
  const sortedProducts = products
    .map(product => ({
      ...product,
      actualStock: parseInt(product.actual_stock),
      percentage: totalActualStock > 0 
        ? ((Math.abs(parseInt(product.actual_stock)) / totalActualStock) * 100).toFixed(1)
        : '0.0'
    }))
    .sort((a, b) => Math.abs(b.actualStock) - Math.abs(a.actualStock));

  // Filter products based on search term
  const filteredProducts = sortedProducts.filter(product =>
    product.product_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get initial products (first 8) and remaining products
  const initialProducts = filteredProducts.slice(0, 8);
  const remainingProducts = filteredProducts.slice(8);

  const handleViewDetails = (productType) => {
    navigate(`/admin/finished-product-warehouse/${encodeURIComponent(productType)}`);
  };

  return (
    <Card extra="p-6">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-100 p-3 rounded-xl">
            <MdInventory className="text-blue-600" size={28} />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-800">Product Distribution</h3>
            <div className="flex items-center space-x-2 mt-1">
              <div className="flex items-center text-gray-400" title="Distribution of products across different types">
                <MdInfo size={16} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MdSearch className="text-gray-400" size={20} />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-6">
        {/* Initial Products (First 8) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {initialProducts.map((product, index) => (
            <div
              key={product.product_type}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleViewDetails(product.product_type)}
            >
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <h5 className="font-semibold text-gray-800">{product.product_type}</h5>
                </div>
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
              </div>
              
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Actual Stock</span>
                  <span className="font-semibold text-gray-800">{product.actualStock.toLocaleString()} boxes</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Percentage</span>
                  <span className="font-semibold text-gray-800">{product.percentage}%</span>
                </div>
              </div>

              <div className="mt-4">
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className="h-2 rounded-full"
                    style={{
                      width: `${product.percentage}%`,
                      backgroundColor: COLORS[index % COLORS.length]
                    }}
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetails(product.product_type);
                  }}
                  className="flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-100 transition-colors"
                >
                  <MdVisibility size={16} />
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* View More Button */}
        {remainingProducts.length > 0 && (
          <div className="flex justify-center pt-4">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
            >
              {isExpanded ? (
                <>
                  <MdExpandLess size={20} />
                  <span>Show Less</span>
                </>
              ) : (
                <>
                  <MdExpandMore size={20} />
                  <span>View More ({remainingProducts.length} more products)</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Remaining Products (shown when expanded) */}
        {isExpanded && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {remainingProducts.map((product, index) => (
              <div
                key={product.product_type}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleViewDetails(product.product_type)}
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <h5 className="font-semibold text-gray-800">{product.product_type}</h5>
                  </div>
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS[(index + 8) % COLORS.length] }}
                  />
                </div>
                
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Actual Stock</span>
                    <span className="font-semibold text-gray-800">{product.actualStock.toLocaleString()} boxes</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Percentage</span>
                    <span className="font-semibold text-gray-800">{product.percentage}%</span>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className="h-2 rounded-full"
                      style={{
                        width: `${product.percentage}%`,
                        backgroundColor: COLORS[(index + 8) % COLORS.length]
                      }}
                    />
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails(product.product_type);
                    }}
                    className="flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-100 transition-colors"
                  >
                    <MdVisibility size={16} />
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

export default ProductDistributionPieChart; 